import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import api from 'api';
import ResolvePagination from 'components/ResolvePagination';
import useQueryParams from 'hooks/useQueryParams';
import { useTranslation } from 'react-i18next';
import { DataRoomInvitation } from 'router/subrouters/DataRoom/models/DataRoom';
import DataRoomInvitationCard from './components/DataRoomInvitationCard';
import ReorderResizeTable from 'components/ReorderResizeTable';
import { BODY_CONTENT_GENERATE } from './components/DataRoomInvitationsTableCells';
import {
  SORTING_VALUES,
  HEADING_TRANSLATIONS,
  TABLE_COLUMNS,
} from './components/DataRoomInvitationsTableCells/data';
import {
  CUSTOM_COL_SIZES,
  REQUIRED_COLUMN,
  DR_INVITATION_LIMIT,
  DR_INVITATION_PAGE,
} from '../TableCells/data';
import {
  getDataRoomRoute,
  safeStringify,
  getIsArrayEmpty,
  getFilterOptionByValue,
} from 'utils';
import { DATA_ROOM_LOGIN_SOURCE } from 'models/DataRoomLoginSource';
import { getApiRangesFromParams } from 'components/RangeFilterWrapper/utils';
import { useNavigate } from 'react-router-dom';
import PortfolioPlaceholderCards from '../../../../components/PortfolioPlaceholderCards';
import { SIZE } from 'models/Size';
import usePageParams from 'hooks/usePageParams';
import useScrollIntoView from 'hooks/useScrollIntoView';
import { TABLES_WITH_COLUMN_SETTINGS } from 'models/User';
import useTableColumnSettings from 'components/ReorderResizeTable/hooks/useTableColumnSettings';
import ManageColumns from 'components/ManageColumns';
import MultiSortPanel from 'components/MultiSortPanel';
import MultiFilterPanel from 'components/MultiFilterPanel';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import useDataRoomInvitationsFilterOptions from './useDataRoomInvitationsFilterOptions';
import { ASCENDING_DESCENDING_COPY, EARLIEST_LATEST_COPY } from 'models/Sort';
import { PF_GRID_VIEW_LIMIT, PRIVATE_DEFAULT_SORT } from '../../../../../data';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import useMultiSort from 'hooks/useMultiSort';
import useTableFetchState from 'hooks/useTableFetchState';
import useChipLabelClickRefs from 'hooks/useChipLabelClickRefs';

import './DataRoomInvitations.styles.scss';

const MULTI_SORT_DIRECTION_COPY = {
  [SORTING_VALUES.price]: ASCENDING_DESCENDING_COPY,
  [SORTING_VALUES.ebitda]: ASCENDING_DESCENDING_COPY,
  [SORTING_VALUES.createdAt]: EARLIEST_LATEST_COPY,
};

type DataRoomInvitationsProps = {
  className?: string;
  isTableView?: boolean;
  isEmpty: boolean;
  setIsEmptyDRInvitation: (value: boolean) => void;
};

const DataRoomInvitations: React.FC<DataRoomInvitationsProps> = (props) => {
  const { className, isTableView, isEmpty, setIsEmptyDRInvitation } = props;

  const classes = classNames('data-room-invitations', className);

  const {
    isLoading,
    totalPages,
    totalItems,
    data: dataRoomInvitations,
    setIsLoading,
    setPaginatedData,
  } = useTableFetchState<DataRoomInvitation>();

  const {
    columns,
    allColumns,
    hiddenColumns,
    columnSizing,
    isDefaultColumns,
    onColumnSizingChange,
    onReorder,
    saveColumnSettings,
    resetToDefault,
  } = useTableColumnSettings(
    TABLES_WITH_COLUMN_SETTINGS.DATA_ROOM_INVITATIONS,
    TABLE_COLUMNS,
  );

  const {
    params: {
      drInvitationSort,
      dataRoomTimeframe,
      dataRoomInvited,
      dataRoomFrom,
      dataRoomTo,
      dataRoomEbitdaFrom,
      dataRoomEbitdaTo,
      dataRoomLocation,
      dataRoomIndustry,
    },
    setQueryParam,
  } = useQueryParams();

  const filterOptions = useDataRoomInvitationsFilterOptions();

  const { limit, updateLimit } = useNumberOfResultsPerPage({
    limitParam: DR_INVITATION_LIMIT,
    pageParam: DR_INVITATION_PAGE,
  });

  const dataLimit = useMemo(
    () => `${isTableView ? limit : PF_GRID_VIEW_LIMIT}`,
    [isTableView, limit],
  );

  const [contentRef, scrollToStart] = useScrollIntoView();

  const drInvitationPage = usePageParams(DR_INVITATION_PAGE);

  const { multiSortValues, multiSortDirectionTranslation } = useMultiSort(
    columns,
    SORTING_VALUES,
    HEADING_TRANSLATIONS,
    MULTI_SORT_DIRECTION_COPY,
  );

  const columnFilterOptions = useMemo(
    () => ({
      price: {
        options: getFilterOptionByValue(filterOptions, 'dataRoomTo'),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.price],
      },
      ebitda: {
        options: getFilterOptionByValue(filterOptions, 'dataRoomEbitdaTo'),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.ebitda],
      },
      state: {
        options: getFilterOptionByValue(filterOptions, 'dataRoomLocation'),
      },
      industry: {
        options: getFilterOptionByValue(filterOptions, 'dataRoomIndustry'),
      },
      createdAt: {
        options: getFilterOptionByValue(filterOptions, 'dataRoomInvited'),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.createdAt],
      },
    }),
    [filterOptions, multiSortDirectionTranslation],
  );

  const renderDeals = useMemo(
    () =>
      isLoading ? (
        <PortfolioPlaceholderCards
          numberOfCards={+dataLimit}
          size={SIZE.SMALL}
        />
      ) : (
        dataRoomInvitations.map(
          ({
            listing,
            dealRoom: {
              _id,
              portfolioName,
              industry,
              dealOutcome,
              otherDealOutcome,
              dataRoomId,
            },
          }) => (
            <DataRoomInvitationCard
              key={_id}
              title={portfolioName}
              industry={industry}
              dealOutcome={dealOutcome}
              otherDealOutcome={otherDealOutcome}
              dataRoomId={dataRoomId}
              imageSrc={listing?.gallery?.[0]?.url}
            />
          ),
        )
      ),
    [dataLimit, dataRoomInvitations, isLoading],
  );

  const tableContent = useMemo(
    () =>
      TABLE_COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: ({ dealRoom, listing, createdAt }: DataRoomInvitation) =>
            BODY_CONTENT_GENERATE[col]({
              dealRoom,
              listing,
              createdAt,
            }),
        }),
        {},
      ),
    [],
  );

  const navigate = useNavigate();

  const { t } = useTranslation();

  const {
    multiFilterRef,
    multiSortRef,
    onChipLabelClick,
    onSortChipLabelClick,
  } = useChipLabelClickRefs();

  const hasActiveFilters = useMemo(
    () =>
      !!(
        dataRoomLocation ||
        dataRoomIndustry ||
        dataRoomFrom ||
        dataRoomTo ||
        dataRoomEbitdaFrom ||
        dataRoomEbitdaTo ||
        dataRoomTimeframe ||
        dataRoomInvited
      ),
    [
      dataRoomLocation,
      dataRoomIndustry,
      dataRoomFrom,
      dataRoomTo,
      dataRoomEbitdaFrom,
      dataRoomEbitdaTo,
      dataRoomTimeframe,
      dataRoomInvited,
    ],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const priceRanges = getApiRangesFromParams(dataRoomFrom, dataRoomTo);
        const ebitdaRanges = getApiRangesFromParams(
          dataRoomEbitdaFrom,
          dataRoomEbitdaTo,
        );

        const { data } = await api.portfolio.buyer.getDataRoomInvitations({
          $relations: '["listing"]',
          $page: drInvitationPage,
          $sort: drInvitationSort || PRIVATE_DEFAULT_SORT,
          $limit: dataLimit,
          timeframe: dataRoomTimeframe,
          invited: dataRoomInvited,
          priceRanges: safeStringify(priceRanges),
          ebitdaRanges: safeStringify(ebitdaRanges),
          location: dataRoomLocation,
          industry: dataRoomIndustry,
        });

        setPaginatedData(data);
        setIsEmptyDRInvitation(getIsArrayEmpty(data.items));
      } catch (e) {
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    drInvitationPage,
    drInvitationSort,
    dataRoomTimeframe,
    dataRoomInvited,
    dataRoomFrom,
    dataRoomTo,
    dataRoomEbitdaFrom,
    dataRoomEbitdaTo,
    dataRoomLocation,
    dataRoomIndustry,
    dataLimit,
    setIsEmptyDRInvitation,
    setIsLoading,
    setPaginatedData,
  ]);

  if (isEmpty && !hasActiveFilters) return null;

  return (
    <div className={classes} ref={contentRef}>
      <div className="table-heading">
        <p className="title">{t('dataRoomInvitations')}</p>

        <div className="browse-portfolio-heading__right">
          {isTableView && !isEmpty && (
            <NumberOfResultsPerPageFilter
              limit={limit}
              updateLimit={updateLimit}
            />
          )}
          <MultiFilterPanel ref={multiFilterRef} filters={filterOptions} />
          <MultiSortPanel
            ref={multiSortRef}
            options={multiSortValues}
            sortDirectionTranslations={multiSortDirectionTranslation}
            sortParam="drInvitationSort"
          />
          {isTableView && (
            <ManageColumns
              allColumns={allColumns}
              hiddenColumns={hiddenColumns}
              headingTranslations={HEADING_TRANSLATIONS}
              lockedColumn={REQUIRED_COLUMN}
              isDefaultColumns={isDefaultColumns}
              onSave={saveColumnSettings}
              onResetToDefault={resetToDefault}
            />
          )}
        </div>
      </div>

      <ActiveFilterSortPills
        filterOptions={filterOptions}
        sortOptions={multiSortValues}
        sortParam="drInvitationSort"
        onChipLabelClick={onChipLabelClick}
        onSortChipLabelClick={onSortChipLabelClick}
      />

      <div className="resolve-page-items">
        {!isTableView ? (
          <>
            {renderDeals}
            <ResolvePagination
              pageParam={DR_INVITATION_PAGE}
              totalPages={totalPages}
              onClick={scrollToStart}
            />
          </>
        ) : (
          <ReorderResizeTable
            className="portfolio-results-table"
            headingTranslations={HEADING_TRANSLATIONS}
            tableContentTransformations={tableContent}
            columns={columns as Array<keyof DataRoomInvitation>}
            sortingValues={SORTING_VALUES}
            columnFilterOptions={columnFilterOptions}
            isDataLoading={isLoading}
            data={dataRoomInvitations}
            customSizes={CUSTOM_COL_SIZES}
            sort={drInvitationSort}
            totalPages={totalPages}
            totalItems={totalItems}
            recordName="invitation"
            pageParam={DR_INVITATION_PAGE}
            updateSort={(value) => setQueryParam('drInvitationSort', value)}
            onRowClick={({ dataRoomId }) =>
              navigate(
                getDataRoomRoute(
                  dataRoomId,
                  undefined,
                  DATA_ROOM_LOGIN_SOURCE.PORTFOLIO,
                ),
              )
            }
            onReorder={
              onReorder as (columns: Array<keyof DataRoomInvitation>) => void
            }
            columnSizing={columnSizing}
            onColumnSizingChange={onColumnSizingChange}
            onPaginationClick={scrollToStart}
          />
        )}
      </div>
    </div>
  );
};

export default DataRoomInvitations;
